import React, {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  useMemo,
} from "react";
import { AxiosError } from "axios";
import { captureExceptionEvent } from "shared-services";
import { useTranslation } from "i18n";
import CommonErrorPopup from "../../CommonErrorPopup";

interface ErrorBoundaryContextType {
  errorCode: number | undefined;
  setErrorCode: (code: number) => void;
  setCloseAction: Dispatch<SetStateAction<(() => void) | undefined>>;
}

const ErrorBoundaryContext = createContext<
  ErrorBoundaryContextType | undefined
>(undefined);

export const useErrorBoundary = () => {
  const ctx = useContext(ErrorBoundaryContext);

  if (!ctx)
    throw new Error(
      "useErrorBoundary must be used within ErrorBoundaryProvider"
    );

  return (error: AxiosError, closeAction?: () => void) => {
    captureExceptionEvent(error);
    ctx.setErrorCode(error?.response?.status || 0);

    if (closeAction) {
      ctx.setCloseAction(() => closeAction);
    }
  };
};

const ErrorBoundaryProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const [errorCode, setErrorCode] = useState<number>();
  const [closeAction, setCloseAction] = useState<() => void>();

  const onClose = () => {
    setErrorCode(undefined);
    closeAction?.();
    setCloseAction(undefined);
  };

  const contextValue = useMemo(
    () => ({ errorCode, setErrorCode, setCloseAction }),
    [errorCode, setErrorCode, setCloseAction]
  );

  return (
    <ErrorBoundaryContext.Provider value={contextValue}>
      {errorCode && (
        <CommonErrorPopup
          errorStatusCode={errorCode}
          popupProps={{
            close: onClose,
          }}
          alertMessageProps={{
            buttons: [
              {
                name: t("common:button.label.close"),
                type: "primary",
                click: onClose,
              },
            ],
          }}
        />
      )}
      {children}
    </ErrorBoundaryContext.Provider>
  );
};

export default ErrorBoundaryProvider;
